import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * PoolMergeCard — Phase E Convergence splash.
 *
 * Displays as a floating overlay in the top-right corner of the dashboard
 * whenever a fresh pool_merge event lands. Uses the same paper-cutout +
 * receipt-printer animation as the AlertCard so the "printed a ticket"
 * language carries. Auto-dismisses after ~5.5 s so the stack doesn't grow.
 *
 * Owned by AppShell (data source: useCarpoolRequests().merges[0] +
 * latestMergeAt). Only renders when a newer merge has been observed since
 * the component was last shown.
 */
import { useEffect, useState } from "react";
const rootStyle = {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "22px 22px 20px 22px",
    minWidth: 320,
    maxWidth: 380,
    background: "var(--color-clay-ember)",
    color: "var(--color-pure-white)",
    fontFamily: "var(--font-mono, 'JetBrains Mono', monospace)",
    backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 3px)",
    animation: "bb-ticket-print 1.4s cubic-bezier(0.22, 0.8, 0.28, 1) both",
    transformOrigin: "top center",
    willChange: "clip-path, transform",
};
const labelStyle = {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    opacity: 0.9,
};
const valueBigStyle = {
    fontFamily: "var(--font-display), sans-serif",
    fontSize: 26,
    fontWeight: 900,
    letterSpacing: "-0.015em",
    lineHeight: 1.1,
    margin: 0,
};
const rowStyle = {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 12,
};
const kpiRowStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 12,
    marginTop: 4,
};
const kpiStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 2,
};
const kpiLabelStyle = {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    opacity: 0.75,
};
const kpiValueStyle = {
    fontSize: 20,
    fontWeight: 800,
    letterSpacing: "0.01em",
    fontFamily: "var(--font-mono, monospace)",
};
export function PoolMergeCard({ merge }) {
    return (_jsxs("article", { className: "bb-paper-cutout", style: rootStyle, "data-testid": `pool-merge-${merge.joined_request_id}`, children: [_jsxs("div", { style: rowStyle, children: [_jsx("span", { style: labelStyle, children: "POOL MERGE" }), _jsx("span", { style: labelStyle, children: "+1 RIDER" })] }), _jsx("div", { "aria-hidden": "true", style: {
                    height: 1,
                    background: "rgba(255,255,255,0.35)",
                    margin: "2px 0",
                } }), _jsxs("p", { style: valueBigStyle, children: [merge.driver_name ?? "Driver", " \u00B7 ", merge.driver_id ?? "—"] }), _jsxs("div", { style: kpiRowStyle, children: [_jsxs("div", { style: kpiStyle, children: [_jsx("span", { style: kpiLabelStyle, children: "DETOUR" }), _jsxs("span", { style: kpiValueStyle, children: ["+", merge.detour_km.toFixed(2), " km"] })] }), _jsxs("div", { style: kpiStyle, children: [_jsx("span", { style: kpiLabelStyle, children: "SAVES" }), _jsxs("span", { style: kpiValueStyle, children: ["\u20B9", merge.inr_saved.toFixed(0)] })] }), _jsxs("div", { style: kpiStyle, children: [_jsx("span", { style: kpiLabelStyle, children: "CO\u2082 \u2193" }), _jsxs("span", { style: kpiValueStyle, children: [merge.co2_saved_kg.toFixed(1), " kg"] })] })] })] }));
}
/**
 * PoolMergeSplash — top-level container that decides *when* to show the
 * card. Watches `latestMergeAt` and displays the newest merge for ~5.5 s
 * before dismissing.
 */
export function PoolMergeSplash({ merges, latestMergeAt, }) {
    const [displayedAt, setDisplayedAt] = useState(null);
    useEffect(() => {
        if (latestMergeAt == null)
            return undefined;
        setDisplayedAt(latestMergeAt);
        const t = window.setTimeout(() => setDisplayedAt(null), 5500);
        return () => window.clearTimeout(t);
    }, [latestMergeAt]);
    if (displayedAt == null || merges.length === 0)
        return null;
    const merge = merges[0];
    if (!merge || merge.at !== displayedAt)
        return null;
    return (_jsx("div", { style: {
            position: "fixed",
            top: 88,
            right: 24,
            zIndex: 3200,
            pointerEvents: "none",
        }, "aria-live": "polite", children: _jsx(PoolMergeCard, { merge: merge }) }));
}
